import { useCallback, useLayoutEffect, useState } from "react";
import {
  Body,
  Small,
  SubTitle,
  SubTitle2,
} from "@vestaboard/installables/lib/components/Typography";
import {
  ActionButton,
  Button,
  Cadence,
  CadencePicker,
  DayOfWeek,
  Divider,
  IBoardStyle,
  ISwatchValue,
  Input,
  Interval,
  MessagePreviewModal,
  NotificationBanner,
  Spacer,
  SwatchPicker,
  ToggleButton,
  parseTimeString,
  useToasts,
} from "@vestaboard/installables";
import { Box, CircularProgress } from "@mui/material";
import { makeVestaboardStyles } from "@vestaboard/installables/lib/hooks/useVestaboardStyles";
import {
  DataProviders,
  IConnectedAccounts,
  checkSetupComplete,
  displayNow,
  saveSubscription,
} from "../api";
import { useSubscription } from "../hooks/useSubscription";
import { YoutubeModal } from "../components/YoutubeModal";
import { ConnectAccountSection } from "../components/ConnectAccountSection";
import { useParams } from "react-router-dom";
import { getSettingsStrings } from "../dataProviderContstants";
import { useIsQuietHours } from "../hooks/useQuietHours";

const useStyles = makeVestaboardStyles(() => ({
  section: {
    width: "100%",
    maxWidth: 600,
    display: "flex",
    flexDirection: "column",
    paddingTop: "24px",
    marginLeft: "16px",
    marginRight: "16px",
  },
  content: {
    display: "flex",
    flexDirection: "column",
    minHeight: "100vh",
    alignItems: "center",
    height: "100%",
    padding: "16px",
    paddingTop: "32px",
    paddingBottom: "32px",
    backgroundColor: "#25282a",
  },
  descriptionContainer: {
    textAlign: "left",
    width: "100%",
    maxWidth: 600,
  },
}));

export const Settings = () => {
  const classes = useStyles();
  const { singledataprovider } = useParams<{ singledataprovider?: string }>();
  const strings = getSettingsStrings(singledataprovider);

  const [openPreview, setOpenPreview] = useState(false);
  const [updateOn, setUpdateOn] = useState("repeating_cadence");

  const [openModal, setOpenModal] = useState<DataProviders | undefined>();
  const [messageColor, setMessageColor] = useState<ISwatchValue>(69); // default white
  const [messageHeader, setMessageHeader] = useState<string>("");
  const [connectedAccounts, setConnectedAccounts] =
    useState<IConnectedAccounts>({});

  const [setupComplete, setSetupComplete] = useState(false);
  const [fetchedSettings, setFetchedSettings] = useState(false);

  const {
    boardId,
    subscriptionConfigToken,
    subscriptionId,
    loading,
    boardTitle,
    boardStyle,
    boardPreview,
  } = useSubscription();

  const { formattedQuietHours, isQuietHours } = useIsQuietHours({
    subscriptionId,
    subscriptionConfigToken,
  });

  const [cadence, setCadence] = useState<Cadence | undefined>({
    interval: Interval.Hour,
    startAtTime: parseTimeString("11:55"),
    endAtTime: parseTimeString("12:55"),
    daysOfTheWeek: [
      DayOfWeek.Monday,
      DayOfWeek.Tuesday,
      DayOfWeek.Wednesday,
      DayOfWeek.Thursday,
      DayOfWeek.Friday,
    ],
  });

  const { addToast } = useToasts();

  const fetchSettings = useCallback(async () => {
    boardId &&
      subscriptionConfigToken &&
      subscriptionId &&
      (await checkSetupComplete(
        boardId,
        subscriptionId,
        subscriptionConfigToken,
      )
        .then(
          ({
            setupComplete,
            connectedAccounts,
            cadence,
            messageHeader,
            messageColor,
            updateOnNewFollows,
            promptSetup,
          }) => {
            connectedAccounts && setConnectedAccounts(connectedAccounts);
            cadence && setCadence(cadence);
            setSetupComplete(setupComplete);
            messageColor && setMessageColor(messageColor);
            messageHeader && setMessageHeader(messageHeader);
            promptSetup && setOpenModal(promptSetup);
            updateOnNewFollows
              ? setUpdateOn("new_follows")
              : cadence?.interval && setUpdateOn("repeating_cadence");
          },
        )
        .catch(() => {
          console.warn("no settings found yet");
        }));
  }, [boardId, subscriptionId, subscriptionConfigToken]);

  useLayoutEffect(() => {
    // fetch once on load
    if (
      !setupComplete &&
      boardId &&
      subscriptionId &&
      subscriptionConfigToken &&
      !loading
    ) {
      fetchSettings().then(() => {
        setFetchedSettings(true);
      });
    }
  }, [
    boardId,
    subscriptionId,
    subscriptionConfigToken,
    fetchSettings,
    setupComplete,
    loading
  ]);

  // dont render cadence picker until cadence is set.
  if (loading || !fetchedSettings) {
    return (
      <Box sx={classes.content}>
        <Box sx={classes.descriptionContainer}>
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  return (
    <>
      <Box sx={classes.content}>
        <Box sx={classes.descriptionContainer}>
          <NotificationBanner
            text={formattedQuietHours}
            visible={isQuietHours}
          />
          <SubTitle>Description</SubTitle>
          <Spacer size={"large"} />
          <Body>{strings.settingsDetails}</Body>
          <Spacer size={"medium"} />
          <Box style={{ display: "flex", flexDirection: "row" }}>
            <ActionButton
              onClick={() => {
                setOpenPreview(true);
              }}
            >
              Preview
            </ActionButton>
            <div style={{ width: 16 }}></div>
            <ActionButton
              disabled={
                Object.keys(connectedAccounts || {}).length === 0 ||
                !setupComplete
              }
              onClick={async () => {
                if (isQuietHours) {
                  addToast(formattedQuietHours, {
                    appearance: "error",
                  });
                  return;
                }

                subscriptionConfigToken &&
                  subscriptionId &&
                  (await displayNow(
                    boardId,
                    subscriptionId,
                    subscriptionConfigToken,
                  ));
              }}
            >
              Display Now
            </ActionButton>
          </Box>
          <Spacer size={"large"} />
          <Divider />
          <Spacer size={"medium"} />
          <SubTitle>{strings.settingsHeader}</SubTitle>
          <Spacer size={"medium"} />
        </Box>
        {/* TODO MODALS INSTEAD OF SECTIONS */}
        <ConnectAccountSection
          connectedAccounts={connectedAccounts}
          dataProvider={DataProviders.facebook}
          title={"Connect Facebook Business Page"}
          fetchSettings={fetchSettings}
          setOpenModal={setOpenModal}
          serviceName={"Facebook"}
        />
        <ConnectAccountSection
          connectedAccounts={connectedAccounts}
          dataProvider={DataProviders.instagram}
          title={"Connect Instagram Business"}
          detailText={
            "Connect your Instagram account tied to your Facebook Page"
          }
          fetchSettings={fetchSettings}
          setOpenModal={() => {
            // Facebook and Instagram use the same modal
            setOpenModal(DataProviders.facebook);
          }}
          serviceName={"Instagram"}
        />

        <ConnectAccountSection
          connectedAccounts={connectedAccounts}
          dataProvider={DataProviders.youtube}
          title={"Connect YouTube Channel"}
          fetchSettings={fetchSettings}
          setOpenModal={setOpenModal}
          serviceName={"YouTube"}
        />
        <ConnectAccountSection
          connectedAccounts={connectedAccounts}
          dataProvider={DataProviders.twitch}
          title={"Connect Twitch Channel"}
          fetchSettings={fetchSettings}
          setOpenModal={setOpenModal}
          serviceName={"Twitch"}
        />

        <ConnectAccountSection
          connectedAccounts={connectedAccounts}
          dataProvider={DataProviders.patreon}
          title={"Connect Patreon Campaign"}
          fetchSettings={fetchSettings}
          setOpenModal={setOpenModal}
          serviceName={"Patreon"}
        />

        <Box sx={classes.section}>
          <>
            <Divider />
            <Spacer size={"medium"} />
            <SubTitle2>Header</SubTitle2>
            <Spacer size={"medium"} />
            <Input
              placeholder={
                boardStyle?.includes("note")
                  ? strings.defaultNoteCTA
                  : strings.defaultCTA
              }
              onValueChange={(text) =>
                text.length <= (boardStyle?.includes("note") ? 13 : 22) &&
                setMessageHeader(text)
              }
              value={messageHeader}
              max={boardStyle?.includes("note") ? "13" : "22"}
            ></Input>
          </>
          <Spacer size={"medium"} />
          {!singledataprovider && (
            <>
              <SubTitle2>Line Separator Color</SubTitle2>
              <Spacer size={"medium"} />
              <SwatchPicker
                value={messageColor}
                setValue={setMessageColor}
                hideEditTools={true}
                spaceEvenly={true}
                endPaintMode={() => {}}
                isPaintMode={false}
                rainbowSwatch={true}
              />
              <Spacer size={"medium"} />
              <Box style={{ textAlign: "center" }}>
                <Small>
                  Choose the color of the line separator shown below the header
                  text.
                </Small>
              </Box>
            </>
          )}
        </Box>
        <Box sx={classes.section}>
          <Divider />
          <Spacer size={"medium"} />
          <SubTitle2>Frequency</SubTitle2>
          <Spacer size={"medium"} />
          {singledataprovider === "youtube" ||
          singledataprovider === "facebook" ? (
            <></>
          ) : (
            <>
              <ToggleButton
                condenseOnMobile={false}
                options={[
                  {
                    value: "new_follows",
                    key: "New Follows",
                  },
                  {
                    value: "repeating_cadence",
                    key: "Repeating Cadence",
                  },
                ]}
                onToggle={setUpdateOn}
                value={updateOn}
              />
              <Spacer size={"medium"} />
            </>
          )}
          {updateOn === "repeating_cadence" && (
            <CadencePicker
              allowWeekdaySelection
              defaultDays={[
                DayOfWeek.Monday,
                DayOfWeek.Tuesday,
                DayOfWeek.Wednesday,
                DayOfWeek.Thursday,
                DayOfWeek.Friday,
                DayOfWeek.Saturday,
                DayOfWeek.Sunday,
              ]}
              intervals={[
                // Interval.Minutes_15,
                // Interval.Minutes_20,
                Interval.Minutes_30,
                Interval.Hour,
                Interval.Hour_4,
                Interval.Day,
                Interval.Week,
              ]}
              cadence={cadence}
              onChange={(cadence) => {
                setCadence(cadence);
              }}
            />
          )}
        </Box>
        <Spacer size={"medium"} />
        <Box sx={classes.section}>
          <Spacer size={"large"} />
          <Button
            buttonType="white"
            disabled={!cadence}
            onClick={async () => {
              if (subscriptionId && subscriptionConfigToken) {
                const dataProviders = Object.keys(
                  connectedAccounts,
                ) as Array<DataProviders>;

                if (cadence)
                  await saveSubscription({
                    subscriptionConfigToken,
                    subscriptionId,
                    boardId,
                    boardTitle,
                    dataProviders,
                    updateOnNewFollows: updateOn === "new_follows",
                    boardStyle,
                    cadence,
                    messageHeader,
                    messageColor,
                  })
                    ?.then(() => {
                      addToast("Channel updated", { appearance: "success" });
                    })
                    ?.catch((err) => {
                      console.error(err);
                      addToast("Error updating channel", {
                        appearance: "error",
                      });
                    });
              } else {
                addToast("Please select a cadence", {
                  appearance: "error",
                });
              }
            }}
          >
            {"Save Changes"}
          </Button>
        </Box>
        <Spacer size={"large"} />
      </Box>
      {/* Conditional render to avoid fetching on load */}
      {/* {openModal === DataProviders.facebook && (
        <FacebookModal
          key={`${openModal}-facebook`}
          visible={openModal === DataProviders.facebook}
          onSuccess={async () => await fetchSettings()}
          onClose={() => {
            setOpenModal(undefined);
          }}
        />
      )} */}
      <YoutubeModal
        key={`${openModal}-youtube`}
        visible={openModal === DataProviders.youtube}
        saveSubscription={async () => {
          if (!subscriptionId || !subscriptionConfigToken) {
            return;
          }
          const dataProviders = Object.keys(
            connectedAccounts,
          ) as Array<DataProviders>;
          await saveSubscription({
            subscriptionConfigToken,
            subscriptionId,
            boardId,
            boardTitle,
            dataProviders: Array.from(new Set([...dataProviders, DataProviders.youtube])),
            updateOnNewFollows: updateOn === "new_follows",
            boardStyle,
            cadence: cadence || {
              interval: Interval.Hour,
              startAtTime: parseTimeString("11:55"),
              endAtTime: parseTimeString("12:55"),
              daysOfTheWeek: [
                DayOfWeek.Monday,
                DayOfWeek.Tuesday,
                DayOfWeek.Wednesday,
                DayOfWeek.Thursday,
                DayOfWeek.Friday,
              ],
            },
            messageHeader,
            messageColor,
          })
            ?.then(() => {
              addToast("Channel updated", { appearance: "success" });
            })
            ?.catch((err) => {
              console.error(err);
              addToast("Error updating channel", {
                appearance: "error",
              });
            });
        }}
        onSuccess={async (channelName) => {
          await fetchSettings();
          const channel = `@${channelName}`;
          const truncatedChannelName = channel?.substring(0, 15);
          boardStyle?.includes("note") &&
            singledataprovider &&
            truncatedChannelName &&
            setMessageHeader(truncatedChannelName);
        }}
        onClose={() => {
          setOpenModal(undefined);
        }}
      />
      {/* <TwitchModal
        key={`${openModal}-twitch`}
        visible={openModal === DataProviders.twitch}
        onSuccess={async () => await fetchSettings()}
        onClose={() => {
          setOpenModal(undefined);
        }}
        setOpenModal={setOpenModal}
      /> */}
      {/* 
      <PatreonModal
        key={`${openModal}-patreon`}
        visible={openModal === DataProviders.patreon}
        onSuccess={async () => await fetchSettings()}
        onClose={() => {
          setOpenModal(undefined);
        }}
        setOpenModal={setOpenModal}
      /> */}

      <MessagePreviewModal
        visible={openPreview}
        onClose={function (): void {
          setOpenPreview(false);
        }}
        boardStyle={(boardStyle || "black") as IBoardStyle}
        characters={boardPreview}
      />
    </>
  );
};
